// src/features/dashboard/hooks/Manager/useAtRiskAssociates.ts

import { useCallback, useEffect, useState } from 'react';

import { useDashboardDate } from '../../../context/DashboardDateContext';
import { formatApiDate } from '../../../utils/date.utils';

import {
  AtRiskKpiRequest,
  RiskByTeamRequest,
  AssociateRegisterRequest,
  ReasonBreakdownRequest,
  RiskTrendRequest,
  AtRiskKpiResponse,
  TeamRiskResponse,
  AssociateRiskResponse,
  ReasonBreakdownResponse,
  RiskTrendResponse,
} from '../../../types/Manager/atRiskAssociates.types';

import { getAtRiskKpiCards } from '../../../services/Manager/kpiCards.service';
import { getAtRiskByTeam } from '../../../services/Manager/riskByTeam.service';
import { getAtRiskAssociateRegister } from '../../../services/Manager/associateRegister.service';
import { getAtRiskReasonBreakdown } from '../../../services/Manager/reasonBreakdown.service';
import { getRiskTrend } from '../../../services/Manager/riskTrend.service';

interface UseAtRiskAssociatesProps {
  managerEmployeeCode: string;
}

const useAtRiskAssociates = ({
  managerEmployeeCode,
}: UseAtRiskAssociatesProps) => {
  const { fromDate, toDate } = useDashboardDate();

  const [loading, setLoading] = useState(false);

  const [kpiCards, setKpiCards] =
    useState<AtRiskKpiResponse | null>(null);

  const [teamData, setTeamData] =
    useState<TeamRiskResponse[]>([]);

  const [associateRegister, setAssociateRegister] =
    useState<AssociateRiskResponse[]>([]);

  const [reasonBreakdown, setReasonBreakdown] =
    useState<ReasonBreakdownResponse[]>([]);

  const [riskTrend, setRiskTrend] =
    useState<RiskTrendResponse[]>([]);

  const [kpiError, setKpiError] =
    useState<string | null>(null);

  const [teamError, setTeamError] =
    useState<string | null>(null);

  const [associateError, setAssociateError] =
    useState<string | null>(null);

  const [reasonError, setReasonError] =
    useState<string | null>(null);

  const [riskTrendError, setRiskTrendError] =
    useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!managerEmployeeCode || !fromDate || !toDate) {
      return;
    }

    setLoading(true);

    setKpiError(null);
    setTeamError(null);
    setAssociateError(null);
    setReasonError(null);
    setRiskTrendError(null);

    const formattedFromDate = formatApiDate(fromDate);
    const formattedToDate = formatApiDate(toDate);

    const commonRequest = {
      managerEmployeeCode,
      fromDate: formattedFromDate,
      toDate: formattedToDate,
      productivityThreshold: 60,
      noOfDays: 3,
      idleThreshold: 30,
      activeHoursThreshold: 7,
      nonProdThreshold: 20,
      attendanceThreshold: 70,
    };

    const kpiRequest: AtRiskKpiRequest = {
      ...commonRequest,
    };

    const teamRequest: RiskByTeamRequest = {
      ...commonRequest,
    };

    const associateRequest: AssociateRegisterRequest = {
      ...commonRequest,
    };

    const reasonRequest: ReasonBreakdownRequest = {
      ...commonRequest,
    };

    const riskTrendRequest: RiskTrendRequest = {
      managerEmployeeCode,
      currentDate: formattedToDate,
      productivityThreshold: 80,
      noOfDays: 3,
      idleThreshold: 30,
      activeHoursThreshold: 7,
      nonProdThreshold: 20,
      attendanceThreshold: 70,
    };

    const [
      kpiResult,
      teamResult,
      associateResult,
      reasonResult,
      trendResult,
    ] = await Promise.allSettled([
      getAtRiskKpiCards(kpiRequest),
      getAtRiskByTeam(teamRequest),
      getAtRiskAssociateRegister(associateRequest),
      getAtRiskReasonBreakdown(reasonRequest),
      getRiskTrend(riskTrendRequest),
    ]);

    if (kpiResult.status === 'fulfilled') {
      setKpiCards(kpiResult.value);
    } else {
      console.error(kpiResult.reason);
      setKpiCards(null);
      setKpiError('Unable to load KPI Cards.');
    }

    if (teamResult.status === 'fulfilled') {
      setTeamData(teamResult.value);
    } else {
      console.error(teamResult.reason);
      setTeamData([]);
      setTeamError('Unable to load Risk By Team.');
    }

    if (associateResult.status === 'fulfilled') {
      setAssociateRegister(associateResult.value);
    } else {
      console.error(associateResult.reason);
      setAssociateRegister([]);
      setAssociateError('Unable to load Associate Register.');
    }

    if (reasonResult.status === 'fulfilled') {
      setReasonBreakdown(reasonResult.value);
    } else {
      console.error(reasonResult.reason);
      setReasonBreakdown([]);
      setReasonError('Unable to load Risk Reason Breakdown.');
    }

    if (trendResult.status === 'fulfilled') {
      setRiskTrend(trendResult.value);
    } else {
      console.error(trendResult.reason);
      setRiskTrend([]);
      setRiskTrendError('Unable to load Risk Trend.');
    }

    setLoading(false);
  }, [managerEmployeeCode, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,

    kpiCards,
    teamData,
    associateRegister,
    reasonBreakdown,
    riskTrend,

    kpiError,
    teamError,
    associateError,
    reasonError,
    riskTrendError,

    refetch: fetchData,
  };
};

export default useAtRiskAssociates;